/**
 *
 * TotalTestElapsedTime
 *
 * A functional component that tracks and displays the total elapsed time for the test
 *
 * totalElapsedTestTime is the value for the entire test duration, including multi day tests.
 * totalElapsedSessionTime is the value for the current session, which may be less than the totalElapsedTestTime.
 *
 * When a test is started for the first time, only the totalElapsedSessionTime is set to zero. The totalElapsedTestTime is continued from the last session.
 */
import { intlShape, injectIntl } from 'react-intl';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import * as progressTimerHelper from 'helpers/progressTimerHelper';
import { selectedInstanceSelector } from '../../containers/DirectionsPage/selectors';
import {
  getTotalElapsedTestTime,
  getTotalElapsedSessionTime,
  setTotalElapsedTestTime,
  setTotalElapsedSessionTime,
} from '../../helpers/persistence/examManagementHelper';
import {
  getIsNewSession,
  isTimerAlreadyReset,
  markTimerReset,
} from '../../helpers/persistence/repoManagementHelper';
import {
  updateTotalElapsedTime,
  setTestTimeWarningTriggered,
  setTestTimeExpirationTriggered,
} from '../../containers/DirectionsPage/actions';
import { config } from '../../config';
import './index.css';
import messages from './messages';

/**
 *
 */
function getInitialSessionElapsedTime(testInstanceId = null) {
  const breakTimeTimerType = 'sessionTime';

  // Check if this is a new session AND this specific timer hasn't been reset yet
  if (getIsNewSession() && !isTimerAlreadyReset(breakTimeTimerType)) {
    // Reset the persisted session time
    setTotalElapsedSessionTime(0);
    // Mark only this specific timer as reset
    markTimerReset(breakTimeTimerType);
    // Return 0 to start with a fresh session timer
    return 0;
  }

  // For continuing sessions or if already reset, use the stored session elapsed time

  // in cases the instance is not available, we want the default value to be the currentTime from this components state.
  const elapsedSessionTime = getTotalElapsedSessionTime({
    testInstanceId,
    defaultValue: 0,
  });
  return elapsedSessionTime;
}

/**
 * Get the initial total test elapsed time from persistence
 */
function totalElapsedTestTimeFromPersistence(testInstanceId = null) {
  // in cases the instance is not available, we want the default value to be the currentTime from this components state.
  const elapsedTestTime = getTotalElapsedTestTime({
    testInstanceId,
    defaultValue: 0,
  });
  return elapsedTestTime;
}

/**
 * Total Test Elapsed Time UI component
 * Tracks and displays the total elapsed time for the test
 */
function TotalTestElapsedTime({
  intl,
  isActive,
  displayTestTime,
  onTestTimeExpired,
  onTestTimeWarning,
  instanceId = 'default',
  updateTotalElapsedTimeAction,
  setTestTimeWarningTriggeredAction,
  setTestTimeExpirationTriggeredAction,
  selectedInstance,
}) {
  const SESSION_TIMER_NAME = `total-session-timer-${instanceId}`;
  const TOTAL_TEST_TIMER_NAME = `total-test-timer-${instanceId}`;

  const warningTriggeredRef = useRef(false);
  const expirationTriggeredRef = useRef(false);

  const MAX_TEST_DURATION = config.REACT_APP_TOTAL_TEST_ELAPSED_LIMIT_MINUTES; // 480 minutes (8 hours)
  const TEST_WARNING_THRESHOLD =
    config.REACT_APP_TOTAL_TEST_ELAPSED_WARNING_BEFORE_END_MINUTES; // 60 minutes (1 hour)

  // Constants for time thresholds (in milliseconds)
  const MAX_TEST_DURATION_MS = MAX_TEST_DURATION * 60 * 1000;
  const ONE_HOUR_MS = TEST_WARNING_THRESHOLD * 60 * 1000;
  const WARNING_THRESHOLD_MS = MAX_TEST_DURATION_MS - ONE_HOUR_MS; // When 1 hour remains before expiration
  const selectedTestInstanceId = selectedInstance?.testInstanceId || null;
  // Rename for clarity
  const EXPIRATION_THRESHOLD_MS = MAX_TEST_DURATION_MS;

  // Get the current elapsed time in milliseconds
  const initialElapsedSessionTimeMs = getInitialSessionElapsedTime(
    selectedTestInstanceId,
  );
  const initialElapsedTestTimeMs = totalElapsedTestTimeFromPersistence(
    selectedTestInstanceId,
  );
  const lastActiveChangeRef = useRef(Date.now());
  const lastPersistedSessionTimeRef = useRef(initialElapsedSessionTimeMs);
  const lastPersistedTestTimeRef = useRef(initialElapsedTestTimeMs);

  // State variables
  const [elapsedTime, setElapsedTime] = useState(initialElapsedSessionTimeMs);

  const saveCurrentTime = useCallback(() => {
    // Get current session time from timer if available, or from state
    const currentSessionTime = progressTimerHelper.getTimeRemaining(
      SESSION_TIMER_NAME,
      elapsedTime,
    );

    // Get current test time from timer if available
    const currentTestTime = progressTimerHelper.getTimeRemaining(
      TOTAL_TEST_TIMER_NAME,
      initialElapsedTestTimeMs,
    );

    // Store in persistence
    setTotalElapsedSessionTime(currentSessionTime, selectedTestInstanceId);
    setTotalElapsedTestTime(currentTestTime, selectedTestInstanceId);
    lastPersistedSessionTimeRef.current = currentSessionTime;
    lastPersistedTestTimeRef.current = currentTestTime;

    return { sessionTime: currentSessionTime, testTime: currentTestTime };
  }, [
    elapsedTime,
    initialElapsedTestTimeMs,
    SESSION_TIMER_NAME,
    TOTAL_TEST_TIMER_NAME,
  ]);

  /**
   * Callback function for the session timer
   */
  const updateSessionTime = useCallback(
    currentTimeMs => {
      setElapsedTime(currentTimeMs);
      // Store elapsed time in milliseconds with each tick
      setTotalElapsedSessionTime(currentTimeMs, selectedTestInstanceId);
      lastPersistedSessionTimeRef.current = currentTimeMs;

      // Check for warning threshold (1 hour)
      if (
        onTestTimeWarning &&
        !warningTriggeredRef.current &&
        currentTimeMs >= WARNING_THRESHOLD_MS
      ) {
        onTestTimeWarning();
        setTestTimeWarningTriggeredAction(true);
        warningTriggeredRef.current = true;
      }

      // Check for expiration threshold (480 minutes)
      if (
        onTestTimeExpired &&
        !expirationTriggeredRef.current &&
        currentTimeMs >= EXPIRATION_THRESHOLD_MS
      ) {
        onTestTimeExpired();
        setTestTimeExpirationTriggeredAction(true);
        expirationTriggeredRef.current = true;
      }
    },
    [
      onTestTimeWarning,
      onTestTimeExpired,
      WARNING_THRESHOLD_MS,
      EXPIRATION_THRESHOLD_MS,
      setTestTimeWarningTriggeredAction,
      setTestTimeExpirationTriggeredAction,
    ],
  );

  /**
   * Callback function for the total test timer
   * Only persists the value without affecting state or rendering
   */
  const updateTotalTestTime = useCallback(currentTimeMs => {
    // Only update persistence without triggering state updates
    setTotalElapsedTestTime(currentTimeMs, selectedTestInstanceId);
    lastPersistedTestTimeRef.current = currentTimeMs;
  }, []);

  // Reset the warning flags when the component is mounted or when the callbacks change
  useEffect(() => {
    warningTriggeredRef.current = false;
    expirationTriggeredRef.current = false;

    // Check if we should immediately trigger the warnings based on initial time
    if (
      initialElapsedSessionTimeMs >= WARNING_THRESHOLD_MS &&
      onTestTimeWarning
    ) {
      onTestTimeWarning();
      warningTriggeredRef.current = true;
    }

    if (
      initialElapsedSessionTimeMs >= EXPIRATION_THRESHOLD_MS &&
      onTestTimeExpired
    ) {
      onTestTimeExpired();
      expirationTriggeredRef.current = true;
    }
  }, [initialElapsedSessionTimeMs, onTestTimeWarning, onTestTimeExpired]);

  /**
   * Start timer function
   */
  const startTimers = useCallback(() => {
    // Always reset the timers to ensure we have a clean state
    progressTimerHelper.unhookTimer(SESSION_TIMER_NAME);
    progressTimerHelper.unhookTimer(TOTAL_TEST_TIMER_NAME);

    // Get persisted times - these are our sources of truth
    const persistedSessionTimeMs = getInitialSessionElapsedTime(
      selectedTestInstanceId,
    );
    const persistedTestTimeMs = totalElapsedTestTimeFromPersistence(
      selectedTestInstanceId,
    );

    // Start session timer
    progressTimerHelper.hookTimer(
      SESSION_TIMER_NAME,
      0, // not used for unlimited timers
      updateSessionTime,
      true, // unlimited (count up) timer
      persistedSessionTimeMs,
    );

    // Start total test timer - separate from session time
    progressTimerHelper.hookTimer(
      TOTAL_TEST_TIMER_NAME,
      0, // not used for unlimited timers
      updateTotalTestTime,
      true, // unlimited (count up) timer
      persistedTestTimeMs,
    );

    // Update state to match persistence - only session time affects rendering
    setElapsedTime(persistedSessionTimeMs);

    // Record time of timer start
    lastActiveChangeRef.current = Date.now();
  }, [
    updateSessionTime,
    updateTotalTestTime,
    SESSION_TIMER_NAME,
    TOTAL_TEST_TIMER_NAME,
  ]);

  // Periodic save to ensure persistence is updated even during long render periods
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isActive) {
      const persistInterval = setInterval(() => {
        saveCurrentTime();
      }, 10000); // Save every 10 seconds

      return () => clearInterval(persistInterval);
    }
  }, [isActive, saveCurrentTime]);

  // Initialize timer
  useEffect(() => {
    if (isActive) {
      startTimers();
    } else {
      progressTimerHelper.resetTimer(SESSION_TIMER_NAME);
      progressTimerHelper.resetTimer(TOTAL_TEST_TIMER_NAME);
    }

    return () => {
      progressTimerHelper.resetTimer(SESSION_TIMER_NAME);
      progressTimerHelper.resetTimer(TOTAL_TEST_TIMER_NAME);
    };
  }, [isActive, startTimers, SESSION_TIMER_NAME, TOTAL_TEST_TIMER_NAME]);

  useEffect(() => {
    updateTotalElapsedTimeAction(elapsedTime);
  }, [elapsedTime, updateTotalElapsedTimeAction]);

  /**
   * Format time in HH:MM:SS format
   */
  function formatTime(milliseconds) {
    if (milliseconds === undefined) {
      return '--:--:--';
    }

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${
        seconds < 10 ? '0' : ''
      }${seconds}`;
    }

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  return (
    <span
      className={`total-time-elapsed ${displayTestTime ? '' : 'hidden'}`}
      aria-live="off"
    >
      {intl.formatMessage(
        { ...messages.totalTimeElapsed },
        { totalTime: formatTime(elapsedTime) },
      )}
    </span>
  );
}

TotalTestElapsedTime.propTypes = {
  isActive: PropTypes.bool,
  intl: intlShape.isRequired,
  displayTestTime: PropTypes.bool,
  onTestTimeExpired: PropTypes.func,
  onTestTimeWarning: PropTypes.func,
  instanceId: PropTypes.string,
  updateTotalElapsedTimeAction: PropTypes.func.isRequired,
  setTestTimeWarningTriggeredAction: PropTypes.func.isRequired,
  setTestTimeExpirationTriggeredAction: PropTypes.func.isRequired,
  selectedInstance: PropTypes.object,
};

TotalTestElapsedTime.defaultProps = {
  isActive: true,
  displayTestTime: true,
};

const mapStateToProps = createStructuredSelector({
  selectedInstance: selectedInstanceSelector(),
});

const mapDispatchToProps = {
  updateTotalElapsedTimeAction: updateTotalElapsedTime,
  setTestTimeWarningTriggeredAction: setTestTimeWarningTriggered,
  setTestTimeExpirationTriggeredAction: setTestTimeExpirationTriggered,
};

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const MemoizedTotalTestElapsedTime = React.memo(
  TotalTestElapsedTime,
  (prevProps, nextProps) =>
    prevProps.isActive === nextProps.isActive &&
    prevProps.displayTestTime === nextProps.displayTestTime &&
    prevProps.instanceId === nextProps.instanceId &&
    prevProps.selectedInstance?.testInstanceId ===
      nextProps.selectedInstance?.testInstanceId,
);

export default compose(withConnect)(injectIntl(MemoizedTotalTestElapsedTime));
